import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Box from '@mui/joy/Box';
import Typography from '@mui/joy/Typography';
import Card from '@mui/joy/Card';
import CardContent from '@mui/joy/CardContent';
import CardActions from '@mui/joy/CardActions';
import Grid from '@mui/joy/Grid';
import Sheet from '@mui/joy/Sheet';
import Chip from '@mui/joy/Chip';
import IconButton from '@mui/joy/IconButton';
import Button from '@mui/joy/Button';
import Input from '@mui/joy/Input';
import Stack from '@mui/joy/Stack';
import Avatar from '@mui/joy/Avatar';
import Divider from '@mui/joy/Divider';
import Modal from '@mui/joy/Modal';
import ModalDialog from '@mui/joy/ModalDialog';
import ModalClose from '@mui/joy/ModalClose';
import LinearProgress from '@mui/joy/LinearProgress';
import Snackbar from '@mui/joy/Snackbar';

// Icons
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import CloudDownloadRoundedIcon from '@mui/icons-material/CloudDownloadRounded';
import SpaRoundedIcon from '@mui/icons-material/SpaRounded';
import OpacityRoundedIcon from '@mui/icons-material/OpacityRounded';
import InventoryRoundedIcon from '@mui/icons-material/InventoryRounded';
import ImageRoundedIcon from '@mui/icons-material/ImageRounded';

import { useInventory } from '../contexts/InventoryContext';
import { useAuth } from '../contexts/AuthContext';
import { createNotification } from '../api/notifications';
import ItemDialog from './ItemDialog';
import ImportSeedDialog from './ImportSeedDialog';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

export default function InventoryTable({ type }) {
  const { listItems, deleteItem, updateItem } = useInventory();
  const { user } = useAuth();

  // State
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedItems, setSelectedItems] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // Type configurations
  const typeConfig = {
    'PRODUCT-SEED': {
      title: 'Seeds',
      icon: <SpaRoundedIcon />,
      color: 'success',
      gradient: 'linear-gradient(135deg, #C0C0C0 0%, #E5E5E5 100%)', // Platinum gradient
      quantityLabel: 'Unpacked Seeds',
      packagedLabel: 'Seed Packs'
    },
    'PRODUCT-CLONE': {
      title: 'Clones',
      icon: <OpacityRoundedIcon />,
      color: 'warning',
      gradient: 'linear-gradient(135deg, #93e600 0%, #e6f9d0 100%)', // Lime green gradient
      quantityLabel: 'In Progress',
      packagedLabel: 'Ready Clones'
    },
    'SHIPPING_MATERIAL': {
      title: 'Packaging',
      icon: <InventoryRoundedIcon />,
      color: 'neutral',
      gradient: 'linear-gradient(135deg, #9e9e9e 0%, #bdbdbd 100%)',
      quantityLabel: 'Quantity',
      packagedLabel: null
    }
  };

  const config = typeConfig[type] || typeConfig['PRODUCT-SEED'];

  // Load items
  useEffect(() => {
    const loadItems = async () => {
      setLoading(true);
      try {
        const data = await listItems(type);
        setItems(data || []);
        setFilteredItems(data || []);
      } catch (error) {
        console.error('Error loading items:', error);
        setSnackbarMessage(`Failed to load ${config.title.toLowerCase()}: ${error.message}`);
        setSnackbarOpen(true);
        setItems([]);
        setFilteredItems([]);
    } finally {
      setLoading(false);
    }
  };

    loadItems();
  }, [type, listItems]);

  // Update search filter to be more comprehensive
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredItems(items);
    } else {
      const lowercaseTerm = searchTerm.toLowerCase().trim();
      const filtered = items.filter(item => {
        const matchName = item.name?.toLowerCase().includes(lowercaseTerm);
        const matchDescription = item.description?.toLowerCase().includes(lowercaseTerm);
        const matchLocation = item.location?.toLowerCase().includes(lowercaseTerm);
        const matchUnit = item.unit?.toLowerCase().includes(lowercaseTerm);
        
        return matchName || matchDescription || matchLocation || matchUnit;
      });
      setFilteredItems(filtered);
    }
  }, [searchTerm, items]);

  const handleDeleteSelected = async () => {
    try {
      await Promise.all(selectedItems.map(id => deleteItem(type, id)));
      setSelectedItems([]);
      setDeleteModalOpen(false);
      setSnackbarMessage(`Successfully deleted ${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''}`);
      setSnackbarOpen(true);
      // Reload items
      const data = await listItems(type);
      setItems(data || []);
      setFilteredItems(data || []);
    } catch (error) {
      console.error('Error deleting items:', error);
      setSnackbarMessage(`Failed to delete items: ${error.message}`);
      setSnackbarOpen(true);
      setDeleteModalOpen(false);
    }
  };

  const toggleItemSelection = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleAddItem = () => {
    setEditingItem(null);
    setItemDialogOpen(true);
  };

  const handleEditItem = (item) => {
    setEditingItem({ ...item, isNew: false });
    setItemDialogOpen(true);
  };

  const handleImportItems = () => {
    setImportDialogOpen(true);
  };

  const handleImportSelect = (seed) => {
    // Convert seed to inventory item format
    const inventoryItem = {
      itemType: type,
      itemId: seed.id,
      name: seed.name,
      description: seed.description,
      quantity: 0,
      packagedCount: seed.inventory || 0,
      imageUrls: seed.imageUrl ? [seed.imageUrl] : [],
      productRefId: seed.id,
      productCreatedDate: seed.createdDate,
      isNew: true
    };
    setEditingItem(inventoryItem);
    setImportDialogOpen(false);
    setItemDialogOpen(true);
  };

  const handleItemSave = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
    setItemDialogOpen(false);
    setEditingItem(null);
    // Reload items
    const loadItems = async () => {
      try {
        const data = await listItems(type);
        setItems(data);
        setFilteredItems(data);
      } catch (error) {
        console.error('Error reloading items:', error);
      }
    };
    loadItems();
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 20
      }
    }
  };

  if (loading) {
    return (
        <Box sx={{ 
        minHeight: '100vh', 
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        pt: 10
      }}>
        <Stack spacing={2} alignItems="center">
          {config.icon}
          <Typography level="h4" color="primary">Loading {config.title}...</Typography>
          <LinearProgress sx={{ width: 200 }} />
        </Stack>
        </Box>
    );
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      pt: 10,
      pb: 4,
      px: { xs: 2, sm: 3, md: 4 }
    }}>
      {/* Header */}
      <MotionBox
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        sx={{ mb: 4 }}
      >
        <Sheet
          variant="soft"
          sx={{
            p: 3,
            borderRadius: 'xl',
            background: 'linear-gradient(135deg, #C0C0C0 0%, #E5E5E5 100%)',
            position: 'relative',
            overflow: 'hidden'
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 2, textAlign: 'center' }}>
            <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} sx={{ mb: 2 }}>
              <Avatar
                variant="soft"
          sx={{ 
                  bgcolor: 'rgba(0, 0, 0, 0.1)',
                  color: '#333333',
                  width: 48,
                  height: 48
                }}
              >
                {config.icon}
              </Avatar>
              <Box>
                <Typography 
                  level="h1" 
          sx={{ 
                    fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                    fontWeight: 'bold',
            color: '#333333',
                    mb: 0.5
                  }}
                >
                  {config.title} Inventory
            </Typography>
                <Typography level="body-lg" sx={{ color: '#666666' }}>
                  Manage your {config.title.toLowerCase()} inventory
        </Typography>
              </Box>
            </Stack>
        </Box>
        </Sheet>
      </MotionBox>

      {/* Search and Actions */}
      <MotionBox
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.1 }}
        sx={{ mb: 4 }}
      >
        <Sheet
          variant="soft"
          sx={{ 
            p: 3,
            borderRadius: 'xl',
            background: 'rgba(255, 255, 255, 0.05)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'center' }}
            justifyContent="space-between"
        sx={{ 
              '@media (max-width: 600px)': {
                alignItems: 'center'
              }
            }}
          >
            {/* Search */}
            <Input
              placeholder={`Search ${config.title.toLowerCase()}...`}
              startDecorator={<SearchRoundedIcon />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ 
                minWidth: { xs: '100%', sm: 300 },
                borderRadius: 'lg'
              }}
            />

            {/* Actions */}
            <Stack 
              direction="row" 
              spacing={1} 
              sx={{
                '@media (max-width: 600px)': {
                  flexDirection: 'column',
                  alignItems: 'center',
                  width: '100%'
                }
              }}
            >
              {(type === 'PRODUCT-SEED' || type === 'PRODUCT-CLONE') && (
                <Button
                  variant="soft"
                  color={config.color}
                  startDecorator={<CloudDownloadRoundedIcon />}
                  onClick={handleImportItems}
                  sx={{
                    borderRadius: 'lg',
                    '@media (max-width: 600px)': {
                      width: '100%'
                    }
                  }}
                >
                  Import
                </Button>
              )}
              
              <Button
                variant="solid"
                color="primary"
                startDecorator={<AddRoundedIcon />}
                onClick={handleAddItem}
                sx={{
                  borderRadius: 'lg',
                  '@media (max-width: 600px)': {
                    width: '100%'
                  }
                }}
              >
                Add {config.title.slice(0, -1)}
              </Button>
              
              {selectedItems.length > 0 && (
              <Button
                  variant="solid"
                  color="danger"
                  startDecorator={<DeleteRoundedIcon />}
                  onClick={() => setDeleteModalOpen(true)}
                sx={{
                    borderRadius: 'lg',
                    '@media (max-width: 600px)': {
                      width: '100%'
                    }
                  }}
                >
                  Delete ({selectedItems.length})
              </Button>
              )}
            </Stack>
          </Stack>
        </Sheet>
      </MotionBox>

      {/* Items Grid */}
      {filteredItems.length === 0 ? (
        <MotionBox
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <Sheet
            variant="soft"
            sx={{ 
              p: 6,
              borderRadius: 'xl',
              textAlign: 'center', 
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
          >
            <Avatar
              variant="soft"
              color={config.color}
              sx={{ width: 80, height: 80, mx: 'auto', mb: 2 }}
            >
              {config.icon}
            </Avatar>
            <Typography level="h3" sx={{ mb: 1, color: 'text.primary' }}>
              No {config.title} Found
            </Typography>
            <Typography level="body-lg" sx={{ color: 'text.secondary', mb: 3 }}>
              {searchTerm ? 'No items match your search.' : `Get started by adding ${config.title.toLowerCase()} to your inventory.`}
            </Typography>
              <Button
              variant="solid"
              color="primary"
              startDecorator={<AddRoundedIcon />}
              size="lg"
              onClick={handleAddItem}
              sx={{ borderRadius: 'lg' }}
            >
              Add {config.title.slice(0, -1)}
            </Button>
          </Sheet>
        </MotionBox>
      ) : (
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <Grid container spacing={3}>
            <AnimatePresence>
              {filteredItems.map((item) => {
                const isSelected = selectedItems.includes(item.itemId);
                const isLowStock = item.quantity <= (item.reorderThreshold || 0);
                
                return (
                  <Grid key={item.itemId} xs={12} sm={6} lg={4}>
                    <MotionCard
                      variants={itemVariants}
                      layout
                      whileHover={{ 
                        scale: 1.02,
                        transition: { type: 'spring', stiffness: 300, damping: 20 }
                      }}
                      onClick={() => toggleItemSelection(item.itemId)}
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        border: isSelected ? `2px solid` : '1px solid',
                        borderColor: isSelected ? 'primary.500' : 'neutral.300',
                        background: isSelected 
                          ? 'rgba(33, 150, 243, 0.1)' 
                          : 'rgba(255, 255, 255, 0.05)',
                        backdropFilter: 'blur(10px)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                    >
                      {/* Image */}
                      {(item.imageUrls && item.imageUrls.length > 0 && item.imageUrls[0]) ? (
                        <Box
                          component="img"
                          src={item.imageUrls[0]}
                          alt={item.name}
                          sx={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover'
                          }}
                        />
                      ) : (
                        <Box
                sx={{
                            width: '100%',
                            height: 200,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(255, 255, 255, 0.1)',
                            color: 'text.secondary'
                          }}
                        >
                          <ImageRoundedIcon sx={{ fontSize: 48 }} />
                        </Box>
                      )}

                      <CardActions 
                        sx={{ 
                          p: 2, 
                          pt: 0, 
                          position: 'absolute', 
                          bottom: 8, 
                          right: 8, 
                          display: 'flex', 
                          gap: 1,
                          zIndex: 10
                        }}
                      >
                        <IconButton
                          variant="soft"
                          color="primary"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditItem(item);
                          }}
                        >
                          <EditRoundedIcon />
                        </IconButton>
                        
                        <IconButton
                          variant="soft"
                          color="danger"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedItems([item.itemId]);
                            setDeleteModalOpen(true);
                          }}
                        >
                          <DeleteRoundedIcon />
                        </IconButton>
                      </CardActions>

                      {/* Increase card height to prevent text overlap */}
                      <CardContent 
                        sx={{ 
                          p: 2, 
                          pb: 8,  // Increase bottom padding to make room for buttons
                          height: '100%',
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'space-between'
                        }}
                      >
                        {/* Header */}
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
                          <Box sx={{ flex: 1, textAlign: 'center' }}>
                            <Typography level="title-md" sx={{ fontWeight: 'bold', mb: 0.5, textAlign: 'center' }}>
                              {item.name}
                            </Typography>
                            {item.description && (
                              <Typography 
                                level="body-sm" 
                  sx={{
                                  color: 'text.secondary',
                                  display: '-webkit-box',
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: 'vertical',
                                  overflow: 'hidden',
                                  textAlign: 'center'
                                }}
                              >
                                {item.description}
                              </Typography>
              )}
            </Box>
                          
                          {isLowStock && (
                            <Chip
                              variant="soft"
                              color="danger"
                              size="sm"
                              sx={{ ml: 1 }}
                            >
                              Low Stock
                            </Chip>
                          )}
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        {/* Stats */}
                        <Stack spacing={2}>
                          <Stack direction="row" justifyContent="space-between">
                            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                              {config.quantityLabel}
                            </Typography>
                            <Typography level="title-sm" sx={{ fontWeight: 'bold' }}>
                              {item.quantity?.toLocaleString() || 0}
                            </Typography>
                          </Stack>

                          {config.packagedLabel && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                {config.packagedLabel}
                              </Typography>
                              <Typography level="title-sm" sx={{ fontWeight: 'bold' }}>
                                {item.packagedCount?.toLocaleString() || 0}
                              </Typography>
                            </Stack>
                          )}

                          {item.location && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                Location
                              </Typography>
                              <Chip variant="soft" size="sm">
                                {item.location}
                              </Chip>
                            </Stack>
                          )}

                          {item.unit && (
                            <Stack direction="row" justifyContent="space-between">
                              <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                                Unit
                              </Typography>
                              <Chip variant="soft" size="sm">
                                {item.unit}
                              </Chip>
                            </Stack>
                          )}
                        </Stack>
                      </CardContent>

                      {/* Selection indicator */}
                      {isSelected && (
                        <motion.div
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ 
                            scale: 1, 
                            opacity: 1,
                            boxShadow: '0 0 20px 5px rgba(33, 150, 243, 0.5)' // Glowing blue border
                          }}
                          style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            border: '3px solid rgba(33, 150, 243, 0.7)',
                            borderRadius: 'inherit',
                            pointerEvents: 'none',
                            zIndex: 10
            }}
          />
        )}
                    </MotionCard>
                  </Grid>
                );
              })}
            </AnimatePresence>
          </Grid>
        </motion.div>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <ModalDialog
          variant="soft"
          sx={{
            maxWidth: 400,
            background: 'rgba(0, 0, 0, 0.9)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}
        >
          <ModalClose />
          
          <Typography level="h4" sx={{ mb: 2, color: 'white' }}>
              Confirm Deletion
          </Typography>
          
          <Typography level="body-md" sx={{ mb: 3, color: 'text.secondary' }}>
            Are you sure you want to delete {selectedItems.length} item{selectedItems.length > 1 ? 's' : ''}? 
            This action cannot be undone.
          </Typography>

          <Stack direction="row" spacing={2} justifyContent="flex-end">
          <Button 
              variant="soft"
              color="neutral"
              onClick={() => setDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button 
              variant="solid"
              color="danger"
            onClick={handleDeleteSelected}
          >
            Delete
          </Button>
          </Stack>
        </ModalDialog>
      </Modal>

      {/* Item Dialog */}
      <ItemDialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        onSave={handleItemSave}
        type={type}
        initialData={editingItem}
      />

      {/* Import Dialog */}
      <ImportSeedDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSelect={handleImportSelect}
        inventoryType={type}
      />

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        onClose={handleCloseSnackbar}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        color={snackbarMessage.includes('failed') || snackbarMessage.includes('error') ? 'danger' : 'success'}
      >
        {snackbarMessage}
      </Snackbar>
    </Box>
  );
}
